import PageHeader from '../components/PageHeader.jsx'
import { IconArrow } from '../components/icons.jsx'
import { useAccess } from '../lib/access.jsx'
import {
  TOP_TIER_TEAMS,
  AUDIENCE,
  TIER,
  featuredTeam,
  rosterTeams,
  displayUrl,
} from '../lib/topTierTeams.js'
import styles from './TopTierTeams.module.css'

/**
 * TOP TIER TEAMS — the hub's home for SR's elite / priority accounts.
 *
 * A READ-ONLY LAUNCHER, on purpose, and the same pattern as the Pricing and
 * Presentations tabs: this page holds no account data, embeds no portal, and
 * carries no pricing or cost logic. Each surface it links to already has its own
 * sign-in and is already the source of truth for what it does — the ring portal
 * owns the selection, the film portal owns the viewing record, the rings console
 * owns published pricing. Re-implementing any of them here would give the hub a
 * second, drifting copy.
 *
 * Everything below is driven off `lib/topTierTeams.js`. To add a team, add an
 * object to that array — no change in this file.
 */
export default function TopTierTeams() {
  const { can, why } = useAccess()
  const hero = featuredTeam()
  const roster = rosterTeams()

  return (
    <div className="fade-up">
      <PageHeader
        eyebrow="Key accounts"
        title="Top Tier Teams"
        lead="SR's elite accounts, each with its own front door. Every link here opens the real, standalone surface for that team — the customer's ring selection portal, their presentation film, and the internal console behind them."
      />

      {hero ? <FeaturedTeam team={hero} can={can} why={why} /> : null}

      <section className={styles.roster}>
        <div className={styles.rosterHead}>
          <h2 className={styles.rosterTitle}>The roster</h2>
          <span className={styles.rosterCount}>
            {TOP_TIER_TEAMS.length} {TOP_TIER_TEAMS.length === 1 ? 'account' : 'accounts'}
          </span>
        </div>

        {roster.length ? (
          <div className={styles.grid}>
            {roster.map((team) => (
              <TeamCard key={team.id} team={team} can={can} why={why} />
            ))}
          </div>
        ) : (
          <p className={styles.rosterEmpty}>
            The Hurricanes are the only Top Tier account on the roster today —
            that is the honest count, not a loading state. As more elite accounts
            get a portal and a film, they are added to{' '}
            <span className={styles.mono}>src/lib/topTierTeams.js</span> and
            appear here with the same card and the same links.
          </p>
        )}
      </section>

      <p className={styles.note}>
        Top Tier Teams is a directory, not a second copy of these tools. Ring
        selection, the presentation record, and published ring pricing each stay
        in the application that owns them; this page is where you find the door.
      </p>
    </div>
  )
}

/* ---- The flagship ---------------------------------------------------- */

function FeaturedTeam({ team, can, why }) {
  return (
    <section
      className={styles.hero}
      style={{ '--team-accent': team.accent || 'var(--brass)' }}
    >
      <div className={styles.heroMain}>
        <img
          className={styles.seal}
          src="/brand/sr-monogram.png"
          alt=""
          width="46"
          height="46"
          aria-hidden="true"
        />

        <TierBadge tier={team.tier} featured />

        <h2 className={styles.heroTitle}>{team.name}</h2>
        <p className={styles.heroMeta}>
          {[team.league, team.location, team.program].filter(Boolean).join(' · ')}
        </p>
        <p className={styles.heroLead}>{team.summary}</p>

        {team.status ? (
          <div className={styles.statusRow}>
            <span className={styles.statusDot} />
            {team.status}
          </div>
        ) : null}

        <TeamImage image={team.heroImage} />
      </div>

      <div className={styles.links}>
        <div className={styles.linksHead}>Open a surface</div>
        {team.links.map((link) => (
          <LinkRow key={link.id} link={link} can={can} why={why} />
        ))}
      </div>
    </section>
  )
}

/* ---- A roster card (every non-featured team) ------------------------- */

function TeamCard({ team, can, why }) {
  return (
    <article
      className={styles.card}
      style={{ '--team-accent': team.accent || 'var(--brass)' }}
    >
      <div className={styles.cardHead}>
        <TierBadge tier={team.tier} />
        <h3 className={styles.cardTitle}>{team.name}</h3>
        <p className={styles.cardMeta}>
          {[team.league, team.location].filter(Boolean).join(' · ')}
        </p>
      </div>

      <TeamImage image={team.heroImage} compact />

      {team.status ? <div className={styles.cardStatus}>{team.status}</div> : null}

      <div className={styles.cardLinks}>
        {team.links.map((link) => (
          <LinkRow key={link.id} link={link} can={can} why={why} compact />
        ))}
      </div>
    </article>
  )
}

/* ---- Shared pieces --------------------------------------------------- */

/**
 * The account's featured visual. The asset is OWNED and served from this repo's
 * `public/` — never hot-linked from a club or vendor site, for exactly the same
 * reason we don't borrow crests. Lazy-loaded and async-decoded so a photograph
 * never blocks the page paint; renders nothing at all when a team has no image.
 */
function TeamImage({ image, compact }) {
  if (!image?.src) return null

  return (
    <figure className={`${styles.figure} ${compact ? styles.figureCompact : ''}`}>
      <img
        className={styles.figureImg}
        src={(compact && image.thumbSrc) || image.src}
        alt={image.alt || ''}
        loading="lazy"
        decoding="async"
      />
      {!compact && image.caption ? (
        <figcaption className={styles.figureCaption}>{image.caption}</figcaption>
      ) : null}
    </figure>
  )
}

function TierBadge({ tier, featured }) {
  const isFlagship = tier === TIER.FLAGSHIP
  return (
    <div className={`${styles.badge} ${featured ? styles.badgeOnInk : ''}`}>
      <span className={styles.badgeDot} />
      {isFlagship ? 'Top Tier · Flagship account' : `Top Tier · ${tier}`}
    </div>
  )
}

/**
 * One launcher row. An internal link is gated on its declared RBAC action —
 * navigation hygiene only, exactly as documented in lib/rbac.js: the linked
 * console does its own authorizing. When the check fails we show the row
 * disabled with the reason rather than silently dropping it, so nobody hunts
 * for a link that is deliberately not theirs.
 */
function LinkRow({ link, can, why, compact }) {
  const internal = link.audience === AUDIENCE.INTERNAL
  const allowed = !link.action || can(link.action)

  const body = (
    <>
      <span className={styles.linkText}>
        <span className={styles.linkLabel}>
          {link.label}
          <span className={internal ? styles.tagInternal : styles.tagCustomer}>
            {internal ? 'Internal only' : 'Customer-facing'}
          </span>
        </span>
        {!compact && link.blurb ? (
          <span className={styles.linkBlurb}>{link.blurb}</span>
        ) : null}
        <span className={styles.linkUrl}>{displayUrl(link.href)}</span>
      </span>
      {allowed ? <IconArrow width={16} height={16} className={styles.linkArrow} /> : null}
    </>
  )

  if (!allowed) {
    return (
      <div className={`${styles.link} ${styles.linkDenied}`}>
        {body}
        <span className={styles.denial}>{why(link.action)}</span>
      </div>
    )
  }

  return (
    <a
      className={`${styles.link} ${internal ? styles.linkInternal : ''}`}
      href={link.href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {body}
    </a>
  )
}
